import jsPDF from "jspdf";
import { footerLine, APP_NAME } from "@/lib/branding";
import { COVERINGS } from "@/lib/catalog";

const TABLE_MARGIN = { top: 26, bottom: 12 };
const NORM_LABEL = { wind: "vento", snow: "neve", combinations: "combinazioni", cpe_pitched: "cpe tetti a falda" };
const coveringLabel = (p) => { const c = Object.values(COVERINGS).flat().find((x) => x.v === p.covering); return c ? tr(c.l) : p.covering; };
import autoTable from "jspdf-autotable";
import { COLORS as PLAN_COLORS } from "@/lib/reportPhoto";
import * as XLSX from "xlsx";
import { fmt } from "./catalog";
import { obstacleRect, makeFrame } from "./geo";
import { ZONES } from "./statics";
import { ZONE_COLORS } from "./fields";

import { tr, locale } from "@/lib/i18n";
export const disclaimer = () =>
  tr("AVVISO: Synvora Design fornisce un dimensionamento preliminare e indicativo, NON certificato. Carichi, numero di ancoraggi e zavorre devono essere verificati da un tecnico abilitato secondo NTC 2018 e le condizioni reali di cantiere.");

const BLUE = [15, 82, 186];
const slug = (s) => (s || "progetto").replace(/[^a-z0-9]+/gi, "_").toLowerCase();

const hex = (h) => [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16));

function drawLayout(doc, layout, x, y, w, h, zones) {
  const all = layout.fields.flatMap((f) => f.roof.polygon);
  if (!all.length) return;
  const g = makeFrame(all, 0);
  const toG = (f, rect) => [[rect[0], rect[1]], [rect[2], rect[1]], [rect[2], rect[3]], [rect[0], rect[3]]].map((q) => g.toUV(f.frame.toLL(q)));
  const pts = all.map(g.toUV);
  const us = pts.map((q) => q[0]), vs = pts.map((q) => q[1]);
  const umin = Math.min(...us), vmin = Math.min(...vs);
  const bw = Math.max(...us) - umin || 1, bh = Math.max(...vs) - vmin || 1;
  const s = Math.min(w / bw, h / bh);
  const ox = x + (w - bw * s) / 2, oy = y + (h + bh * s) / 2;
  const P = ([u, v]) => [ox + (u - umin) * s, oy - (v - vmin) * s];
  const poly = (gp, style) => {
    const q = gp.map(P);
    doc.lines(q.slice(1).map((b, i) => [b[0] - q[i][0], b[1] - q[i][1]]), q[0][0], q[0][1], [1, 1], style, true);
  };
  layout.fields.forEach((f) => {
    doc.setDrawColor(30, 41, 59); doc.setLineWidth(0.5);
    poly(f.roof.polygon.map(g.toUV), "S");
    doc.setDrawColor(255, 255, 255); doc.setLineWidth(0.15);
    f.active.forEach((c) => { doc.setFillColor(...(zones ? hex(ZONE_COLORS[c.zone]) : BLUE)); poly(toG(f, c.rect), "FD"); });
    doc.setFillColor(148, 163, 184);
    (f.roof.obstacles || []).forEach((o) => poly(toG(f, obstacleRect(o, f.frame)), "F"));
    const c = P(g.toUV(f.roof.polygon[0]));
    doc.setFontSize(8); doc.setTextColor(15, 23, 42); doc.text(f.roof.name, c[0], c[1] - 2);
  });
  doc.setFontSize(9); doc.setTextColor(15, 23, 42);
  doc.text("N", x + w - 4, y + 2, { align: "center" });
  doc.setDrawColor(15, 23, 42); doc.setLineWidth(0.5); doc.line(x + w - 4, y + 4, x + w - 4, y + 12);
  const bar = 5 * s;
  doc.line(x, y + h + 6, x + bar, y + h + 6);
  doc.setFontSize(8); doc.setTextColor(100, 116, 139); doc.text("5 m", x + bar + 2, y + h + 7);
  if (zones) ZONES.forEach((z, i) => {
    doc.setFillColor(...hex(ZONE_COLORS[z])); doc.rect(x + 30 + i * 28, y + h + 3.5, 4, 4, "F");
    doc.text(tr("Zona {0}", [z]), x + 36 + i * 28, y + h + 7);
  });
}

export function exportPDF(p, layout, bom, eng, extras = {}) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const W = 210;
  const st = bom.stats, env = p.env || {};
  const brand = extras.brand?.name ? extras.brand : { name: APP_NAME, logo: extras.appMark || null, app: true };
  const headed = new Map();
  const header = (title) => {
    const PW = doc.internal.pageSize.getWidth();
    if (!headed.has(doc.getCurrentPageInfo().pageNumber)) headed.set(doc.getCurrentPageInfo().pageNumber, title);
    let x = 14;
    if (brand.logo) {
      const k = Math.min((brand.app ? 44 : 40) / brand.logo.w, (brand.app ? 11 : 13) / brand.logo.h);
      doc.addImage(brand.logo.data, "PNG", x, 3.5 + (13 - brand.logo.h * k) / 2, brand.logo.w * k, brand.logo.h * k);
      x += brand.logo.w * k + 5;
    }
    doc.setFontSize(9); const tw = doc.getTextWidth(title);
    if (brand.app && brand.logo) {
      doc.setTextColor(...BLUE); doc.setFontSize(8); doc.setCharSpace(1.2); doc.text("DESIGN", x - 2, 13); doc.setCharSpace(0);
    } else {
      doc.setTextColor(15, 23, 42); doc.setFontSize(13);
      doc.text(doc.splitTextToSize(brand.profile?.legal_name || brand.name, Math.max(30, PW - 14 - tw - 8 - x))[0], x, 12.5);
    }
    doc.setFontSize(9); doc.setTextColor(...BLUE); doc.text(title, PW - 14, 12.5, { align: "right" });
    doc.setDrawColor(...BLUE); doc.setLineWidth(0.6); doc.line(0, 20, PW, 20); doc.setLineWidth(0.2);
    doc.setTextColor(15, 23, 42);
  };
  const footers = () => {
    const n = doc.getNumberOfPages();
    const line = brand.app ? APP_NAME : `${footerLine(brand.name, brand.profile)} · ${APP_NAME}`;
    for (let i = 1; i <= n; i++) {
      doc.setPage(i);
      if (!headed.has(i)) header(`${headed.get(i - 1).replace(` (${tr("continua")})`, "")} (${tr("continua")})`);
      const PW = doc.internal.pageSize.getWidth(), PH = doc.internal.pageSize.getHeight();
      doc.setDrawColor(226, 232, 240); doc.line(14, PH - 7, PW - 14, PH - 7);
      doc.setFontSize(6.5); doc.setTextColor(100, 116, 139);
      if (line) doc.text(doc.splitTextToSize(line, PW - 60)[0], 14, PH - 3.5);
      doc.text(tr("Pagina {0} di {1}", [i, n]), PW - 14, PH - 3.5, { align: "right" });
    }
  };
  const tbl = (startY, head, body) =>
    autoTable(doc, { margin: TABLE_MARGIN, startY, head: [head], body, theme: "grid", styles: { fontSize: 9 },
      headStyles: { fillColor: BLUE }, columnStyles: { 0: { fontStyle: "bold", cellWidth: 70 } } });

  header(tr("Report tecnico - {0}", [new Date().toLocaleDateString(locale())]));
  doc.setFontSize(16); doc.text(p.name || tr("Progetto"), 14, 32);
  tbl(38, [tr("Dati di progetto"), ""], [
    [tr("Cliente"), p.client || "-"], [tr("Indirizzo"), p.address || "-"],
    [tr("Regione / Provincia"), `${p.region || "-"} / ${p.province || "-"}`],
    [tr("Altitudine"), `${fmt(p.altitude, 0)} ${tr("m s.l.m.")}`], [tr("Altezza edificio"), `${fmt(p.building_height, 1)} m`],
    [tr("Tipo di tetto"), p.roof_type === "piano" ? tr("Tetto piano") : tr("Tetto a falda")], [tr("Copertura"), coveringLabel(p)],
    ...layout.fields.map((f) => [tr("{0} - pendenza / azimut", [f.roof.name]), tr("{0}° / {1}° (Sud = 0°) - {2} m²", [fmt(f.pitch, 1), fmt(f.roof.azimuth, 1), fmt(f.roofArea, 1)])]),
  ]);
  tbl(doc.lastAutoTable.finalY + 6, [tr("Condizioni ambientali (NTC 2018)"), ""], [
    [tr("Zona vento"), tr("{0}  (vb = {1} m/s, categoria esposizione {2})", [env.wind_zone ?? "-", fmt(env.vb), env.exposure ?? "-"])],
    [tr("Pressione cinetica di picco qp"), `${fmt(env.wind_load, 3)} kN/m²`],
    [tr("Zona neve"), tr("{0}  (qsk = {1} kN/m², mu = {2})", [env.snow_zone ?? "-", fmt(env.qsk, 3), fmt(env.mu)])],
    [tr("Carico neve sulla copertura qs"), `${fmt(env.snow_load, 3)} kN/m²`],
  ]);
  const m = layout.module;
  tbl(doc.lastAutoTable.finalY + 6, [tr("Riepilogo impianto"), ""], [
    [tr("Modulo"), `${tr(m.name)} - ${m.power} W - ${m.length} x ${m.width} mm`],
    [tr("Orientamento moduli"), p.modules.orientation === "landscape" ? tr("Orizzontale") : tr("Verticale")],
    [tr("Sistema di montaggio"), layout.flat ? tr("Zavorrato {0}°", [p.modules.tilt]) : tr("Binari con ganci")],
    [tr("Numero moduli"), String(st.modules)], [tr("Potenza totale"), `${fmt(st.kwp)} kWp`],
    [tr("Superficie moduli"), `${fmt(st.moduleArea)} m²`],
    layout.flat
      ? [tr("Zavorra"), tr("{0} kg totali ({1} kg/m²)", [fmt(st.ballastKg, 1), fmt(st.ballastPerM2)])]
      : [tr("Ganci"), tr("{0} pz ({1} /m², interasse minimo {2} m)", [st.hooks || 0, fmt(st.hooksPerM2), fmt(st.hookSpacing)])],
    [tr("Carico permanente impianto"), `${fmt(st.perM2)} kg/m² (${fmt(st.totalKg, 0)} kg)`],
  ]);

  const o = { layout: true, zones: true, ...(extras.opts || {}) };
  if (o.layout || o.zones) {
    doc.addPage("a4", "portrait"); header(tr("Layout del campo moduli"));
    doc.setFontSize(12);
    const both = o.layout && o.zones;
    if (o.layout) { doc.text(tr("Schema dei campi moduli (pianta, nord in alto)"), 14, 32); drawLayout(doc, layout, 20, 42, 170, both ? 105 : 220, false); }
    if (o.zones) { const zy = o.layout ? 167 : 32; doc.text(tr("Zone di carico vento (EN 1991-1-4)"), 14, zy); drawLayout(doc, layout, 20, zy + 5, 170, both ? 95 : 220, true); }
  }

  if (extras.plan) planPage(doc, extras.plan, header, layout.flat);
  for (const sec of extras.sections || []) sectionPage(doc, sec, header, p, !!extras.opts?.section_dims);

  doc.addPage("a4", "portrait"); header(tr("Verifica statica e distinta"));
  const st2 = bom.statics;
  const zr = st2.fields.flatMap((f) => ZONES.filter((z) => f.zones[z]).map((z) => {
    const d = f.zones[z];
    return layout.flat
      ? [f.name, z, d.n, fmt(d.cp), fmt(d.U), fmt(d.H), fmt(d.ballastKg, 1), d.blocks, fmt(d.kgm2, 1)]
      : [f.name, z, d.n, fmt(d.cp), fmt(d.qDown), fmt(d.qUp), `${fmt(d.span)} m`, `${Math.round(d.railUt * 100)}%`, `${Math.round(d.deflUt * 100)}%`, `${Math.round(d.hookUt * 100)}%`];
  }));
  autoTable(doc, { margin: TABLE_MARGIN,
    startY: 28, theme: "grid", styles: { fontSize: 8 }, headStyles: { fillColor: BLUE },
    head: [layout.flat
      ? [tr("Campo"), tr("Zona"), tr("Moduli"), "cp", tr("Sollev. kN"), tr("Spinta kN"), tr("Zavorra kg/mod"), tr("Blocchi"), tr("kg/m²")]
      : [tr("Campo"), tr("Zona"), tr("Moduli"), "cp", tr("q giù SLU"), tr("q su SLU"), tr("Interasse"), tr("Binario"), tr("Freccia"), tr("Gancio")]],
    body: zr,
  });
  doc.setFontSize(8); doc.setTextColor(st2.ok ? 4 : 180, st2.ok ? 120 : 83, st2.ok ? 87 : 9);
  doc.text(st2.ok ? tr("Esito: tutte le zone verificate (stima preliminare).") : tr("Esito: alcune zone NON verificate - rivedere ancoraggi."), 14, doc.lastAutoTable.finalY + 6);
  doc.setTextColor(15, 23, 42);
  autoTable(doc, { margin: TABLE_MARGIN,
    startY: doc.lastAutoTable.finalY + 12, head: [[tr("Codice"), tr("Descrizione"), tr("Q.tà"), "U.M."]],
    body: bom.items.map((i) => [i.code, i.desc, String(i.qty), i.unit]),
    theme: "striped", styles: { fontSize: 9 }, headStyles: { fillColor: BLUE },
  });
  doc.setFontSize(8); doc.setTextColor(180, 83, 9);
  const y = Math.min(doc.lastAutoTable.finalY + 8, 272);
  doc.text(doc.splitTextToSize(disclaimer(), 182), 14, y);
  if (eng) engineSection(doc, eng, header);
  footers();
  doc.save(`${slug(p.name)}_report.pdf`);
}

const rows = (bom) => bom.items.map((i) => ({ [tr("Codice")]: i.code, [tr("Descrizione")]: i.desc, [tr("Quantità")]: Number(i.qty) || 0, [tr("U.M.")]: i.unit }));

export function exportXLSX(p, bom) {
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows(bom)), tr("Distinta materiali").slice(0, 31));
  const s = bom.stats;
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([
    [tr("Progetto"), p.name], [tr("Cliente"), p.client], [tr("Indirizzo"), p.address],
    [tr("Moduli"), s.modules], [tr("Potenza (kWp)"), Number(s.kwp.toFixed(2))], [tr("Peso totale (kg)"), Math.round(s.totalKg)],
    [tr("Nota"), disclaimer()],
  ]), tr("Riepilogo").slice(0, 31));
  XLSX.writeFile(wb, `${slug(p.name)}_distinta.xlsx`);
}

export function exportCSV(p, bom) {
  const lines = [tr("Codice;Descrizione;Quantita;UM"), ...bom.items.map((i) => `${i.code};"${i.desc}";${Number(i.qty) || 0};${i.unit}`)];
  const blob = new Blob(["\uFEFF" + lines.join("\n")], { type: "text/csv;charset=utf-8" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob); a.download = `${slug(p.name)}_distinta.csv`; a.click();
  URL.revokeObjectURL(a.href);
}

const STAT = { incomplete: "INCOMPLETO - dati mancanti", preliminary: "PRELIMINARE - dati non validati", calculated_pending_review: "CALCOLATO - in attesa di revisione ingegneristica" };

function engineSection(doc, e, header) {
  doc.addPage("a4", "portrait"); header(tr("Calcolo strutturale (motore backend)"));
  autoTable(doc, { margin: TABLE_MARGIN,
    startY: 28, theme: "grid", styles: { fontSize: 8 }, headStyles: { fillColor: BLUE }, columnStyles: { 0: { fontStyle: "bold", cellWidth: 55 } },
    head: [[tr("Identificazione del calcolo"), ""]],
    body: [[tr("Stato"), tr(STAT[e.status] || e.status)], [tr("Approvazione strutturale"), tr("NON emessa (il software non rilascia approvazioni)")],
      [tr("Versione motore / norme"), `${e.engine_version} / ${e.norms_version}`], [tr("Hash input"), e.input_hash],
      ...Object.entries(e.standards || {}).map(([k, v]) => [tr("Norma {0}", [tr(NORM_LABEL[k] || k)]), v])],
  });
  if (e.missing.length) autoTable(doc, { margin: TABLE_MARGIN, startY: doc.lastAutoTable.finalY + 4, theme: "grid", styles: { fontSize: 8 }, headStyles: { fillColor: [100, 116, 139] }, head: [[tr("Dati mancanti"), tr("Motivo")]], body: e.missing.map((m) => [m.field, m.reason]) });
  if (e.warnings.length) autoTable(doc, { margin: TABLE_MARGIN, startY: doc.lastAutoTable.finalY + 4, theme: "grid", styles: { fontSize: 8 }, headStyles: { fillColor: [180, 83, 9] }, head: [[tr("Limitazioni")]], body: e.warnings.map((w) => [w]) });
  const rows = e.results.flatMap((r) => r.checks.map((c) => [r.field, r.zone, r.n_modules, c.check, `${c.Ed} / ${c.Rd} ${c.unit}`, `${Math.round(c.utilization * 100)}%`, c.ok ? "OK" : "NO"]));
  if (rows.length) autoTable(doc, { margin: TABLE_MARGIN, startY: doc.lastAutoTable.finalY + 4, theme: "grid", styles: { fontSize: 7 }, headStyles: { fillColor: BLUE }, head: [[tr("Campo"), tr("Zona"), tr("Mod."), tr("Verifica"), tr("Ed / Rd"), tr("Sfrutt."), tr("Esito")]], body: rows });
  autoTable(doc, { margin: TABLE_MARGIN, startY: doc.lastAutoTable.finalY + 4, theme: "grid", styles: { fontSize: 7 }, headStyles: { fillColor: BLUE }, head: [[tr("Passo"), tr("Formula"), tr("Risultato"), tr("Fonte")]],
    body: e.trace.map((s) => [s.title, s.formula, `${s.value} ${s.unit}`, s.source]) });
  doc.setFontSize(8); doc.text(doc.splitTextToSize(e.disclaimer, 182), 14, Math.min(doc.lastAutoTable.finalY + 6, 276));
}

// pagina A4 orizzontale: foto da drone (o mappa satellitare) con falde, moduli, binari, ganci e ostacoli in scala
export const PLAN_BOX = { x: 14, y: 30, w: 222, h: 166 };

function planPage(doc, plan, header, flat) {
  doc.addPage("a4", "landscape");
  header(tr("Pianta su foto - moduli e struttura"));
  const { x, y, w, h } = PLAN_BOX;
  doc.setFontSize(12); doc.setTextColor(15, 23, 42);
  doc.text(plan.source === "foto" ? tr("Foto da drone georeferenziata con impianto e struttura") : tr("Impianto e struttura in pianta"), x, 26);
  doc.addImage(plan.dataUrl, "JPEG", x, y, w, h);
  doc.setDrawColor(148, 163, 184); doc.rect(x, y, w, h);
  doc.setFillColor(255, 255, 255); doc.rect(x + 2, y + h - 8, 72, 6, "F");
  doc.setFontSize(8); doc.setTextColor(15, 23, 42);
  doc.text(plan.source === "foto" ? tr("sfondo: foto da drone georeferenziata") : tr("sfondo: mappa satellitare"), x + 4, y + h - 3.8);
  const L = x + w + 8;
  // nord
  doc.setFillColor(15, 23, 42); doc.triangle(L + 10, y + 2, L + 6, y + 14, L + 14, y + 14, "F");
  doc.setFontSize(11); doc.text("N", L + 10, y + 20, { align: "center" });
  // scala grafica: lunghezza "tonda" ~ 1/4 della larghezza
  const mPerMm = plan.widthM / w;
  const nice = [1, 2, 5, 10, 20, 25, 50, 100].reduce((b, c) => (Math.abs(c / mPerMm - 30) < Math.abs(b / mPerMm - 30) ? c : b), 5);
  const sl = nice / mPerMm, sy = y + 32;
  doc.setFontSize(8); doc.text(tr("Scala grafica"), L, sy - 3);
  for (let i = 0; i < 4; i++) { doc.setFillColor(...(i % 2 ? [255, 255, 255] : [15, 23, 42])); doc.rect(L + (i * sl) / 4, sy, sl / 4, 2.5, "FD"); }
  doc.text("0", L, sy + 6.5); doc.text(`${nice} m`, L + sl, sy + 6.5, { align: "right" });
  doc.text(`1 mm = ${fmt(mPerMm, 2)} m`, L, sy + 11);
  // legenda
  const items = [["roof", tr("Contorno falde"), "line"], ["module", tr("Moduli"), "rect"], ["rail", tr("Binari"), "line"], ["hook", tr("Ganci"), "dot"], ["obstacle", tr("Ostacoli"), "rect"]];
  if (flat) items.splice(2, 2, ["ballast", tr("Zavorra"), "dot"]);
  let ly = sy + 22;
  doc.setFontSize(9); doc.text(tr("Legenda"), L, ly); ly += 6;
  doc.setFontSize(8);
  for (const [key, label, kind] of items) {
    const c = hex(PLAN_COLORS[key]);
    doc.setDrawColor(...c); doc.setFillColor(...c);
    if (kind === "line") { doc.setLineWidth(0.8); doc.line(L, ly - 1, L + 8, ly - 1); doc.setLineWidth(0.2); }
    else if (kind === "rect") doc.rect(L, ly - 3, 8, 4, "F");
    else doc.circle(L + 4, ly - 1, 1.3, "F");
    doc.setTextColor(15, 23, 42); doc.text(label, L + 11, ly); ly += 6.5;
  }
  doc.setDrawColor(220, 38, 38); doc.setLineWidth(0.8); doc.line(L, ly - 1, L + 8, ly - 1); doc.setLineWidth(0.2);
  doc.setFillColor(220, 38, 38); doc.circle(L + 4, ly + 5.5, 1.3, "F");
  doc.text(tr("Struttura non verificata"), L + 11, ly); doc.text(flat ? tr("Zavorra non verificata") : tr("Gancio non verificato"), L + 11, ly + 6.5); ly += 15;
  if (plan.dims) {
    doc.setTextColor(15, 23, 42); doc.text("12,34 m", L, ly); doc.text(tr("Lato falda"), L + 14, ly); ly += 6;
    doc.setDrawColor(250, 204, 21); doc.setLineWidth(0.6); doc.setLineDashPattern([1.2, 0.8], 0); doc.line(L, ly - 1, L + 8, ly - 1); doc.setLineDashPattern([], 0); doc.setLineWidth(0.2);
    doc.text(tr("Distanza min. moduli-bordo"), L + 11, ly); ly += 8;
  }
  if (plan.groupDims) {
    doc.setDrawColor(34, 211, 238); doc.setLineWidth(0.6); doc.line(L, ly - 1, L + 8, ly - 1); doc.setLineWidth(0.2);
    const gl = doc.splitTextToSize(flat ? tr("Fila di moduli: lunghezza della fila e numero di moduli") : tr("Gruppo moduli: larghezza lungo gronda, lunghezza vera lungo falda, righe x colonne (* = gruppo non rettangolare, misure d'ingombro)"), 30);
    doc.setTextColor(15, 23, 42); doc.text(gl, L + 11, ly); ly += doc.getTextDimensions(gl).h + 4;
    if (flat) {
      doc.setDrawColor(244, 114, 182); doc.setLineWidth(0.6); doc.line(L, ly - 1, L + 8, ly - 1); doc.setLineWidth(0.2);
      const rl = doc.splitTextToSize(tr("Spazio libero tra file consecutive (bordo posteriore - bordo anteriore)"), 30);
      doc.text(rl, L + 11, ly); ly += doc.getTextDimensions(rl).h + 4;
    }
  }
  if (plan.sections?.length) {
    doc.setFillColor(239, 68, 68); doc.circle(L + 4, ly - 1, 1.6, "F");
    const sl = doc.splitTextToSize(tr("Linee di sezione {0}", [plan.sections.map((x) => `${x}-${x}`).join(", ")]), 30);
    doc.setTextColor(15, 23, 42); doc.text(sl, L + 11, ly); ly += doc.getTextDimensions(sl).h + 4;
  }
  doc.setTextColor(100, 116, 139); doc.setFontSize(7);
  doc.text(doc.splitTextToSize(tr("Riquadro {0} x {1} m.{2} {3}", [fmt(plan.widthM, 1), fmt(plan.heightM, 1), plan.dims ? tr(" Quote in m, in pianta (proiezione orizzontale).") : "", !plan.hasStruct ? tr("Calcolo strutturale non disponibile: struttura non disegnata.") : flat ? tr("Zavorra dal calcolo strutturale eseguito alla creazione del report.") : tr("Binari e ganci dal calcolo strutturale eseguito alla creazione del report.")]), 40), L, ly);
  doc.setTextColor(15, 23, 42); doc.setDrawColor(0);
}

// sezione verticale della falda perpendicolare alla gronda: misure lungo falda in scala, altezze schematiche
function sectionPage(doc, sec, header, p, dims) {
  doc.addPage("a4", "landscape");
  header(tr("Sezione della falda"));
  doc.setFontSize(12); doc.setTextColor(15, 23, 42);
  doc.text(sec.letter ? tr("Sezione {0}-{1} perpendicolare alla gronda - {2}", [sec.letter, sec.letter, sec.name]) : tr("Sezione perpendicolare alla gronda - {0} (nel mezzo del campo moduli)", [sec.name]), 14, 28);
  const a = (sec.pitch * Math.PI) / 180, X0 = 26, YB = 170, maxW = 235, maxH = 115;
  const k = Math.min(maxW / Math.max(sec.run, 0.5), sec.rise > 0.05 ? maxH / sec.rise : 1e9, 60);
  const d = [Math.cos(a), -Math.sin(a)], n = [-Math.sin(a), -Math.cos(a)];
  const P = (sl, h) => [X0 + d[0] * sl * k + n[0] * h, YB + d[1] * sl * k + n[1] * h];
  const L = sec.length;
  const poly = (pts, style) => doc.lines(pts.slice(1).map((q, i) => [q[0] - pts[i][0], q[1] - pts[i][1]]), pts[0][0], pts[0][1], [1, 1], style, true);
  // travetto e copertura
  doc.setFillColor(214, 186, 140); doc.setDrawColor(120, 90, 50); poly([P(0, -2), P(L, -2), P(L, -7), P(0, -7)], "FD");
  doc.setDrawColor(185, 28, 28); doc.setLineWidth(0.9); doc.line(...P(0, 0), ...P(L, 0)); doc.setLineWidth(0.2);
  // ganci, binari, moduli
  for (const s of sec.rails) {
    doc.setDrawColor(234, 88, 12); doc.setLineWidth(0.7); doc.line(...P(s - 0.08, -0.5), ...P(s, 0.3)); doc.line(...P(s, 0.3), ...P(s, 5)); doc.setLineWidth(0.2);
    doc.setFillColor(30, 41, 59); doc.setDrawColor(15, 23, 42); poly([P(s - 1.5 / k, 5), P(s + 1.5 / k, 5), P(s + 1.5 / k, 7.5), P(s - 1.5 / k, 7.5)], "FD");
  }
  doc.setFillColor(30, 58, 138); doc.setDrawColor(224, 242, 254);
  for (const r of sec.rows) poly([P(r.s0, 7.5), P(r.s1, 7.5), P(r.s1, 9.2), P(r.s0, 9.2)], "FD");
  // pendenza e proiezioni
  doc.setDrawColor(100, 116, 139); doc.setLineDashPattern([1, 1], 0);
  doc.line(X0, YB, X0 + sec.run * k, YB); doc.line(X0 + sec.run * k, YB, X0 + sec.run * k, YB - sec.rise * k); doc.setLineDashPattern([], 0);
  doc.setFontSize(9); doc.setTextColor(15, 23, 42);
  const ar = 14;
  doc.lines(Array.from({ length: 12 }, (_, i) => { const t0 = (a * i) / 12, t1 = (a * (i + 1)) / 12; return [ar * (Math.cos(t1) - Math.cos(t0)), -ar * (Math.sin(t1) - Math.sin(t0))]; }), X0 + ar, YB);
  doc.text(tr("Pendenza {0}°", [fmt(sec.pitch, 1)]), X0 + ar + 2, YB + 4);
  const angle = sec.pitch;
  if (dims) {
    const chain = (vals, h, color) => {
      doc.setDrawColor(...color); doc.setTextColor(...color);
      for (let i = 0; i < vals.length - 1; i++) {
        const s0 = vals[i], s1 = vals[i + 1];
        if (s1 - s0 < 0.005) continue;
        doc.line(...P(s0, h), ...P(s1, h));
        [s0, s1].forEach((s) => doc.line(...P(s, h - 1.2), ...P(s, h + 1.2)));
        const m = P((s0 + s1) / 2, h + 1.8);
        doc.setFontSize(s1 - s0 < 0.25 ? 6 : 7.5); doc.text(fmt(s1 - s0, 2), m[0], m[1], { angle, align: "center" });
      }
    };
    const marks = [0, ...sec.rows.flatMap((r) => [r.s0, r.s1]), L].sort((x, y) => x - y);
    chain(marks, 15, [15, 23, 42]);
    chain([0, ...sec.rails, L].sort((x, y) => x - y), -13, [234, 88, 12]);
    doc.setTextColor(15, 23, 42); doc.setFontSize(8);
    doc.text(tr("proiezione orizzontale {0} m", [fmt(sec.run, 2)]), X0 + (sec.run * k) / 2, YB + 6, { align: "center" });
    doc.text(tr("dislivello {0} m", [fmt(sec.rise, 2)]), X0 + sec.run * k + 3, YB - (sec.rise * k) / 2);
  }
  // legenda e note
  const Lx = 14, Ly = 184;
  const items = [[[30, 58, 138], tr("Modulo")], [[30, 41, 59], tr("Binario")], [[234, 88, 12], tr("Gancio")], [[185, 28, 28], tr("Copertura ({0})", [coveringLabel(p) || "-"])], [[214, 186, 140], tr("Travetto")]];
  doc.setFontSize(8);
  items.forEach(([c, t], i) => { doc.setFillColor(...c); doc.rect(Lx + i * 42, Ly - 3, 5, 3.5, "F"); doc.setTextColor(15, 23, 42); doc.text(t, Lx + i * 42 + 7, Ly); });
  doc.setTextColor(100, 116, 139); doc.setFontSize(7);
  doc.text(doc.splitTextToSize(tr("Misure lungo la falda in m, in scala 1:{0} (riferita alla pagina A4). Spessori e altezze di copertura, gancio, binario e modulo sono SCHEMATICI: il catalogo prodotti non contiene le quote di altezza. Binari a {1} della lunghezza del modulo.{2}", [Math.round(1000 / k), sec.q.map((x) => fmt(x, 2)).join(" / "), dims ? tr(" Quote nere: gronda - moduli - colmo; quote arancioni: posizione dei binari dalla gronda.") : ""]), 268), Lx, Ly + 6);
  doc.setTextColor(15, 23, 42); doc.setDrawColor(0);
}
